import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from './firebase/firebase';
import { toDateValue } from '../utils/relativeTime';

/**
 * Service « Domaines » (mode admin / assistance).
 *
 * Lecture : direct Firestore (l'admin est autorisé par les règles sur
 * `users/{uid}/domains` et `domainRegistrations`) — mêmes patterns que clientService.
 *
 * Actions (disponibilité + réservation POUR le client) : onCall admin → routes admin
 * du deployment-service. Aucune réservation réelle n'est déclenchée sans action admin.
 */

/**
 * Normalise la liste des serveurs DNS renvoyée par OpenProvider.
 * L'API renvoie des objets {seq_nr, name, ip, ip6} ; on ne conserve que les noms
 * (sous la forme d'un tableau de chaînes, sûr pour l'affichage React).
 */
const normalizeNameServers = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((ns) => {
      if (typeof ns === 'string') return ns.trim();
      if (ns && typeof ns === 'object' && typeof ns.name === 'string') return ns.name.trim();
      return null;
    })
    .filter(Boolean);
};

const mapDomain = (id, data) => {
  const providerMetadata = data.providerMetadata || {};
  return {
    id,
    ...data,
    providerMetadata: {
      ...providerMetadata,
      nameServers: normalizeNameServers(providerMetadata.nameServers)
    },
    purchaseDate: toDateValue(data.purchaseDate),
    renewalDate: toDateValue(data.renewalDate),
    createdAt: toDateValue(data.createdAt),
    updatedAt: toDateValue(data.updatedAt),
    failedAt: toDateValue(data.failedAt)
  };
};

const mapTracking = (data) => ({
  ...data,
  createdAt: toDateValue(data.createdAt),
  updatedAt: toDateValue(data.updatedAt)
});

/**
 * Récupère tous les documents domaine d'un client : users/{uid}/domains.
 * @param {string} uid - uid du client (doc users/{uid})
 * @returns {Promise<Array>} Domaines triés par date de création (desc)
 */
export const getUserDomains = async (uid) => {
  if (!uid) return [];
  const snapshot = await getDocs(collection(firestore, 'users', uid, 'domains'));
  return snapshot.docs
    .map((docSnap) => mapDomain(docSnap.id, docSnap.data()))
    .sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
};

/**
 * Récupère le suivi temps réel d'un enregistrement : domainRegistrations/{trackingId}.
 * @param {string} trackingId - ID du doc de tracking (= id du doc domaine)
 * @returns {Promise<Object|null>}
 */
export const getDomainTracking = async (trackingId) => {
  if (!trackingId) return null;
  const snap = await getDoc(doc(firestore, 'domainRegistrations', trackingId));
  if (!snap.exists()) return null;
  return mapTracking(snap.data());
};

/**
 * S'abonne au suivi temps réel d'un enregistrement (étapes de réservation).
 * @param {string} trackingId - ID du doc de tracking
 * @param {(data: Object|null) => void} onData
 * @param {(error: Error) => void} [onError]
 * @returns {Function} Fonction de désabonnement
 */
export const observeDomainTracking = (trackingId, onData, onError) => {
  if (!trackingId) {
    onData?.(null);
    return () => {};
  }
  const ref = doc(firestore, 'domainRegistrations', trackingId);
  return onSnapshot(
    ref,
    (snap) => onData?.(snap.exists() ? mapTracking(snap.data()) : null),
    (error) => onError?.(error)
  );
};

/**
 * Vérifie la disponibilité d'un nom de domaine en .fr et .com (via onCall admin).
 * @param {string} domainName - Nom sans extension (ou avec, l'extension est retirée)
 * @param {string|null} uid - uid du client dont on ouvre la fiche (transmis pour l'audit)
 * @returns {Promise<{domainName: string, domains: Array}>}
 */
export const checkDomainAvailability = async (domainName, uid = null) => {
  const fn = httpsCallable(functions, 'checkDomainAvailabilityAsAdmin');
  const payload = { domainName };
  // uid = client ciblé par l'admin : ajouté au payload pour tracer « qui fait quoi pour qui »
  if (uid) payload.uid = uid;
  const res = await fn(payload);
  return (res && res.data) || { domainName: domainName.trim().toLowerCase(), domains: [] };
};

/**
 * Réserve un domaine POUR le client (via onCall admin → route admin deployment-service).
 * @param {string} uid - uid du client cible (users/{uid})
 * @param {string} domainName - Nom de domaine sans extension
 * @param {string} extension - Extension (.fr | .com)
 * @param {boolean} autoRenew - Renouvellement automatique
 * @returns {Promise<Object>} { success, domain, trackingId, domainId, status, ... }
 */
export const registerDomainForUser = async (uid, domainName, extension, autoRenew = true) => {
  const fn = httpsCallable(functions, 'registerDomainAsUser');
  const res = await fn({ uid, domainName, extension, autoRenew });
  return (res && res.data) || {};
};

/**
 * Supprime le verrou anti-doublon d'un enregistrement en échec (via onCall admin).
 * Seul un document domaine au statut « registration_failed » peut être déverrouillé.
 * @param {string} uid - uid du client cible (users/{uid})
 * @param {string} domainId - id du document domaine (le verrou, = trackingId)
 * @returns {Promise<Object>} { deleted, reason, domainName, message }
 */
export const unlockDomainForUser = async (uid, domainId) => {
  const fn = httpsCallable(functions, 'unlockDomainAsUser');
  const res = await fn({ uid, domainId });
  return (res && res.data) || {};
};

/**
 * Envoie — ou renvoie — au client l'email ② « votre site est en ligne » (onCall admin).
 * Geste MANUEL et EXPLICITE (décision D3c-23) : l'email n'est jamais envoyé par un job, c'est
 * l'admin qui décide du moment, après avoir vérifié que le site répond. `force: true` est la
 * confirmation transmise au serveur (sans elle, l'envoi est refusé).
 * @param {string} uid - uid du client cible (users/{uid})
 * @param {string} domainId - id du document domaine (users/{uid}/domains/{id})
 * @returns {Promise<Object>} { success, sentTo, domainId, domainName, resend, warnings }
 */
export const sendSiteLiveEmailForUser = async (uid, domainId) => {
  const fn = httpsCallable(functions, 'sendSiteLiveEmail');
  const res = await fn({ uid, domainId, force: true });
  return (res && res.data) || {};
};

/**
 * Envoie — ou renvoie — au client l'email ① « votre domaine est réservé » (onCall admin, lot I2c).
 * Même geste MANUEL et EXPLICITE que l'email ② : `force: true` est la confirmation transmise au
 * serveur (sans elle, l'envoi est refusé). Le serveur pose le MÊME marqueur d'idempotence que
 * l'envoi automatique (`notificationSent`) ⇒ aucun doublon possible avec le trigger de création ni
 * avec l'envoi DIFFÉRÉ de la confirmation d'enregistrement.
 * @param {string} uid - uid du client cible (users/{uid})
 * @param {string} domainId - id du document domaine (users/{uid}/domains/{id})
 * @returns {Promise<Object>} { success, sentTo, domainId, domainName, resend, warnings }
 */
export const sendDomainReservedEmailForUser = async (uid, domainId) => {
  const fn = httpsCallable(functions, 'sendDomainReservedEmail');
  const res = await fn({ uid, domainId, force: true });
  return (res && res.data) || {};
};


/**
 * RÉSIDUS d'un domaine (étape 3b.5, décision D3b-14) — INVENTAIRE en LECTURE SEULE.
 *
 * L'inventaire est fait par le module `domainCleanup` du deployment-service (route admin
 * `/api/admin/domains/cleanup-residues`, appelée par la callable `cleanDomainResidues`) : il MESURE
 * les familles de résidus (handle orphelin, bloc nginx, certificat, worker SSL, traces) et il les
 * QUALIFIE (`deletable` / `blocked` / `skipped`) selon les 3 verrous de la doctrine. Aucun drapeau
 * n'est transmis ici : sans `confirm`, la route s'arrête à la mesure (0 écriture, chez personne).
 *
 * @param {string} uid - uid du client cible (users/{uid})
 * @param {Object} target - `{domainId?, domainName?}` (au moins l'un des deux)
 * @returns {Promise<Object>} `{action: 'inspect', dryRun: true, verdict, residues[], plan, errors}`
 */
export const inspectDomainResidues = async (uid, { domainId = null, domainName = null } = {}) => {
  const payload = { uid };
  if (domainId) payload.domainId = domainId;
  if (domainName) payload.domainName = domainName;
  // Aucun drapeau : c'est le mode par défaut, le plus sûr (inventaire seul).
  const res = await httpsCallable(functions, 'cleanDomainResidues')(payload);
  return (res && res.data) || {};
};

/**
 * RÉSIDUS d'un domaine — SIMULATION de nettoyage (`dryRun`), JAMAIS une suppression réelle.
 *
 * `dryRun: true` est le SEUL drapeau que l'écran sait envoyer : le nettoyage réel exige
 * `confirm: true`, que seul un appel direct à la route admin peut porter (décision D3c-20 :
 * « l'admin VOIT, l'automatisme AGIT », aucun bouton de suppression réelle dans le panneau).
 * Le rapport dit donc exactement ce qui SERAIT supprimé — et rien n'est supprimé.
 *
 * @param {string} uid - uid du client cible (users/{uid})
 * @param {Object} target - `{domainId?, domainName?}` (au moins l'un des deux)
 * @returns {Promise<Object>} `{action: 'dry-run', dryRun: true, verdict, removed[], blocked[], errors}`
 */
export const testDomainCleanup = async (uid, { domainId = null, domainName = null } = {}) => {
  const payload = { uid, dryRun: true };
  if (domainId) payload.domainId = domainId;
  if (domainName) payload.domainName = domainName;
  const res = await httpsCallable(functions, 'cleanDomainResidues')(payload);
  return (res && res.data) || {};
};

/**
 * Normalise une erreur d'onCall (HttpsError serveur) pour affichage.
 * Le deployment-service renvoie son code métier (ex: DOMAIN_ALREADY_EXISTS,
 * SUBSCRIPTION_REQUIRED, USER_NOT_FOUND) dans details.error via les fonctions.
 * @param {Error} error - Erreur remontée par httpsCallable
 * @returns {{ code: string, message: string }}
 */
export const mapDomainError = (error) => {
  const details = error?.details || {};
  let code = details.error || error?.code || 'UNKNOWN_ERROR';
  // Compatibilité anciennes versions du SDK : code préfixé « functions/… »
  if (code.startsWith('functions/')) code = code.slice('functions/'.length);
  let message = error?.message || 'Erreur inconnue';
  // Le SDK peut préfixer le message (« INTERNAL : ... »)
  if (message.startsWith('INTERNAL')) message = message.replace(/^INTERNAL\s*:\s*/, '');
  return { code, message };
};
